/**
 * #988 — pure rules for the Polymarket US manual ticket.
 *
 * The ticket keeps every price and quantity as the exact decimal text the
 * trader typed. Validation here mirrors the relay's preparation checks
 * (`relay/src/polymarketUs/orderPreparation.ts`) so an invalid draft never
 * reaches a callback, but it never rewrites a value: off-step input is
 * reported, not rounded, and a NO price stays the NO price. The relay alone
 * compiles the YES-price wire shape.
 *
 * Nothing here is a default borrowed from another venue. A market's minimum,
 * step, tick, durations and post-only support come only from its rules; with
 * no rules there is nothing to validate against and the draft is blocked.
 */
import type { UsAccountContext } from './accountClient';
import { usReason } from './reason';
import type { UsDraft, UsRules, UsScope, UsTarget } from './types';

// ── Exact decimals ─────────────────────────────────────────────────────────

/** 12 fractional digits, the precision the relay and trading client accept. */
const FRACTION_DIGITS = 12;
const SCALE = 10n ** BigInt(FRACTION_DIGITS);
const DECIMAL_TEXT = /^(?:0|[1-9]\d{0,11})(?:\.\d{1,12})?$/;

/** Decimal text → integer units of 1e-12, or null when it isn't plain decimal text. */
export function decimalUnits(text: string): bigint | null {
  if (!DECIMAL_TEXT.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  return BigInt(whole) * SCALE + BigInt(fraction.padEnd(FRACTION_DIGITS, '0'));
}

/** `decimalUnits` with a leading sign: a short holding's negative size
 *  compares below zero instead of reading as unknown (#1226). */
export function signedDecimalUnits(text: string): bigint | null {
  const negative = text.startsWith('-');
  const units = decimalUnits(negative ? text.slice(1) : text);
  return units === null ? null : negative ? -units : units;
}

/** Integer units → shortest exact decimal text (`0.4`, `12`, `0.0655`). */
export function formatUnits(units: bigint): string {
  const whole = units / SCALE;
  const fraction = (units % SCALE).toString().padStart(FRACTION_DIGITS, '0').replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : String(whole);
}

const PRICE_MIN = SCALE / 100n; // $0.01
const PRICE_MAX = (SCALE * 99n) / 100n; // $0.99

/**
 * One tick up or down from a valid price, kept inside $0.01–$0.99. Returns
 * null (no change) when the current text is not a valid price, so the
 * stepper never invents a starting price.
 */
export function stepPrice(text: string, tick: string, direction: 1 | -1): string | null {
  const price = decimalUnits(text);
  const step = decimalUnits(tick);
  if (price === null || step === null || step <= 0n) return null;
  const next = price + step * BigInt(direction);
  if (next < PRICE_MIN || next > PRICE_MAX) return null;
  return formatUnits(next);
}

/**
 * #1006 close: the lowest price this ticket accepts for `outcome` on the
 * market's tick grid — inside $0.01–$0.99, with the grid read on the YES price
 * as `checkTicket` reads it (a NO price is valid when 1 − price is on the
 * tick). A sell limit there fills what the bid book has. Null when no price
 * qualifies or the tick is not plain decimal text.
 */
export function lowestValidPrice(tick: string, outcome: 'yes' | 'no'): string | null {
  const step = decimalUnits(tick);
  if (step === null || step <= 0n || step >= SCALE) return null;
  const price = outcome === 'yes'
    ? ((PRICE_MIN + step - 1n) / step) * step
    : SCALE - ((SCALE - PRICE_MIN) / step) * step;
  return price >= PRICE_MIN && price <= PRICE_MAX ? formatUnits(price) : null;
}

/**
 * #1006 close: the most of `held` this market lets one order sell — rounded
 * DOWN to the quantity step — and what is left over. `sell` is null when even
 * that is below the market's minimum (the holding settles with the market).
 * Exact decimal text throughout; null for text that isn't plain decimal.
 */
export function closeQuantityFor(held: string, step: string, minimum: string): { sell: string | null; remainder: string } | null {
  const h = decimalUnits(held), st = decimalUnits(step), min = decimalUnits(minimum);
  if (h === null || st === null || min === null || st <= 0n || h <= 0n) return null;
  const sell = (h / st) * st;
  if (sell < min || sell <= 0n) return { sell: null, remainder: formatUnits(h) };
  return { sell: formatUnits(sell), remainder: formatUnits(h - sell) };
}

/**
 * #1006 close: how much of the holding the Closing line says this ticket
 * sells, from the Contracts box as it stands — "all 12.5" for the whole
 * holding, "12 of 12.5" for part of it (the market's step, or a size the
 * trader typed), "20 (more than the 12.5 held)" above it, and "— of 12.5"
 * while the box holds no positive plain decimal. Compared exactly, never as
 * floats. Only for a holding the market lets this ticket sell (see
 * `closeQuantityFor`); otherwise the line names no amount.
 */
export function closeSellAmount(entered: string, held: string): string {
  const e = decimalUnits(entered.trim()), h = decimalUnits(held);
  if (e === null || e <= 0n) return `— of ${held}`;
  if (e === h) return `all ${held}`;
  return h !== null && e > h ? `${formatUnits(e)} (more than the ${held} held)` : `${formatUnits(e)} of ${held}`;
}

// ── Durations ──────────────────────────────────────────────────────────────

export const GTD = 'TIME_IN_FORCE_GOOD_TILL_DATE';
const IMMEDIATE = ['TIME_IN_FORCE_IMMEDIATE_OR_CANCEL', 'TIME_IN_FORCE_FILL_OR_KILL'];

/**
 * The durations this ticket can present. DAY is deliberately absent: the
 * venue's documented expiry for it is under review, and a market that lists
 * only DAY offers no duration here rather than a substitute.
 */
const DURATION_LABEL: Record<string, string> = {
  TIME_IN_FORCE_GOOD_TILL_CANCEL: 'Good till canceled',
  TIME_IN_FORCE_GOOD_TILL_DATE: 'Good till date (UTC)',
  TIME_IN_FORCE_IMMEDIATE_OR_CANCEL: 'Immediate or cancel',
  TIME_IN_FORCE_FILL_OR_KILL: 'Fill or kill',
};

export type UsDurationOption = { value: string; label: string };

/** The market's durations the ticket supports, in the market's own order. */
export function durationOptions(rules: UsRules): UsDurationOption[] {
  return rules.timeInForce
    .filter((value) => value in DURATION_LABEL)
    .map((value) => ({ value, label: DURATION_LABEL[value] }));
}

export function durationLabel(value: string): string {
  return DURATION_LABEL[value] ?? value;
}

const DURATION_SHORT: Record<string, string> = {
  TIME_IN_FORCE_GOOD_TILL_CANCEL: 'GTC',
  TIME_IN_FORCE_GOOD_TILL_DATE: 'GTD',
  TIME_IN_FORCE_IMMEDIATE_OR_CANCEL: 'IOC',
  TIME_IN_FORCE_FILL_OR_KILL: 'FOK',
  TIME_IN_FORCE_DAY: 'DAY',
};

/** An exact ISO UTC time as "10/08 00:00 UTC" (seconds only when not zero),
 * read from the text; anything else is shown as given. */
export function formatUtcShort(iso: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?Z$/.exec(iso);
  if (!m) return iso;
  return `${m[1]}/${m[2]} ${m[3]}:${m[4]}${m[5] === '00' ? '' : `:${m[5]}`} UTC`;
}

/** A duration in the order-row vocabulary: "GTC", "GTD 10/08 00:00 UTC", "IOC", "FOK". */
export function durationShort(timeInForce: string, goodTillTime: string | null | undefined): string {
  const short = DURATION_SHORT[timeInForce] ?? timeInForce;
  return timeInForce === GTD && goodTillTime ? `${short} ${formatUtcShort(goodTillTime)}` : short;
}

/** A duration in words: "good till canceled", "good till 10/08 00:00 UTC", "fill or kill". */
export function durationPhrase(timeInForce: string, goodTillTime: string | null | undefined): string {
  if (timeInForce === GTD) return goodTillTime ? `good till ${formatUtcShort(goodTillTime)}` : 'good till date';
  const label = DURATION_LABEL[timeInForce];
  return label ? label.toLowerCase() : durationShort(timeInForce, null);
}

/**
 * A `datetime-local` value read as UTC (the field is labelled UTC) → the
 * exact ISO form the relay accepts, or null when it is not a real instant.
 */
export function utcExpiry(local: string): string | null {
  const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::(\d{2}))?$/.exec(local);
  if (!match) return null;
  const iso = `${match[1]}:${match[2] ?? '00'}.000Z`;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) && new Date(ms).toISOString() === iso ? iso : null;
}

/** The rail's Good to Start expiry: the scheduled start as UTC `datetime-local` text, or '' when unknown. */
export function startExpiryText(startTimeMs: number | null | undefined): string {
  return startTimeMs != null && Number.isFinite(startTimeMs) ? new Date(startTimeMs).toISOString().slice(0, 19) : '';
}

/** Good to Start is offered only for a known future start on a market with a dated duration. */
export function gtsAllowedFor(startTimeMs: number | null | undefined, nowMs: number, rules: Pick<UsRules, 'timeInForce'>): boolean {
  return !!startExpiryText(startTimeMs) && startTimeMs! > nowMs && rules.timeInForce.includes(GTD);
}

const LOCAL_TEXT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * The Game Detail rail reads its custom expiry as the browser's LOCAL time,
 * as the Kalshi and Polymarket tickets do; the entry still holds UTC text
 * (`utcExpiry` above). A `datetime-local` value in local time → the same
 * instant as UTC `datetime-local` text (seconds kept). Text that is not a
 * complete local datetime is returned as given, so it stays visibly invalid.
 */
export function localExpiryToUtc(local: string): string {
  const m = LOCAL_TEXT.exec(local);
  if (!m) return local;
  const at = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? '0'));
  return Number.isFinite(at.getTime()) ? at.toISOString().slice(0, 19) : local;
}

/** The inverse: UTC `datetime-local` text → the same instant in local time, for the rail's field. */
export function utcExpiryToLocal(utc: string): string {
  const iso = utcExpiry(utc);
  if (!iso) return utc;
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// ── Validation ─────────────────────────────────────────────────────────────

export type UsTicketField = 'outcome' | 'action' | 'quantity' | 'price' | 'duration' | 'expiry' | 'postOnly';

/** What the trader has entered. Unchosen fields are null; nothing is preselected. */
export type UsTicketEntry = {
  outcome: 'yes' | 'no' | null;
  action: 'buy' | 'sell' | null;
  quantity: string;
  outcomePrice: string;
  timeInForce: string | null;
  /** Raw `datetime-local` text, read as UTC. Only meaningful for GTD. */
  expiry: string;
  postOnly: boolean;
};

/** The terms a host keeps for a trader across a side flip or a venue-tab
 * round trip, as the international ticket's per-venue slots do: never the
 * price (re-derived from the new side's book), never a check or attempt. */
export type UsDraftTerms = Pick<UsTicketEntry, 'quantity' | 'timeInForce' | 'expiry' | 'postOnly' | 'action'>;

export function draftTermsOf(entry: UsTicketEntry): UsDraftTerms {
  const { quantity, timeInForce, expiry, postOnly, action } = entry;
  return { quantity, timeInForce, expiry, postOnly, action };
}

export const EMPTY_ENTRY: UsTicketEntry = {
  outcome: null,
  action: null,
  quantity: '',
  outcomePrice: '',
  timeInForce: null,
  expiry: '',
  postOnly: false,
};

export type UsTicketCheck = {
  /** Per-field problems, shown inline beside the field. */
  fields: Partial<Record<UsTicketField, string>>;
  /** Account- or market-level reasons no draft can be previewed right now. */
  blockers: string[];
};

/**
 * Account context says what this account may do; it is advisory, and preview
 * and dispatch recheck it. It still gates the ticket: a context that permits
 * nothing must not let a draft through.
 */
export function accountBlockers(context: UsAccountContext): string[] {
  // Institutional enrollment is separate from execution. Its read-only state
  // is not a missing trader permission or a disabled retail key.
  if (context.protocol === 'institutional') {
    // An operator-enabled account whose relay installed native execution is
    // checked like any account; everything else is not available yet.
    if (isNativeExecutable(context)) {
      return !context.permissions.buy ? ['You have no buy permission on this account.'] : [];
    }
    return [context.readiness.state === 'unavailable'
      ? `Trading is unavailable: ${usReason(context.readiness.reason)}.`
      : 'Institutional trading is not available yet.'];
  }
  const reasons: string[] = [];
  if (!context.accountEnabled) reasons.push('This account is disabled.');
  if (context.scopeUse !== 'execution_bound') reasons.push('This account is read-only for your role.');
  if (context.readiness.state === 'unavailable') reasons.push(`Trading is unavailable: ${usReason(context.readiness.reason)}.`);
  else if (!context.readiness.canSubmit) reasons.push('New orders are not available for this account right now.');
  if (!context.permissions.buy && !context.permissions.sell) reasons.push('You have no buy or sell permission on this account.');
  return reasons;
}

/** Native execution available for this ACCOUNT: enabled, execution-bound
 * scope and the relay reporting submit. Account readiness never proves a
 * market or action executable: native sells and markets the relay refuses
 * (unproved mapping, unconfirmed fees) stay in Draft Check with their reason. */
export function isNativeExecutable(context: UsAccountContext): boolean {
  return context.protocol === 'institutional' && context.accountEnabled && context.scopeUse === 'execution_bound'
    && context.readiness.state === 'ready' && context.readiness.canSubmit;
}
/** Why a native sell is not offered: no account-side sell ceiling with
 * settled native position units exists yet. Shown beside Draft Check. */
export const NATIVE_SELL_UNAVAILABLE = 'Selling is not available for institution accounts yet.';

export function checkEntry(
  entry: UsTicketEntry,
  rules: UsRules,
  context: UsAccountContext,
  nowMs: number,
): UsTicketCheck {
  return checkEntryWithBlockers(entry, rules, context, nowMs, accountBlockers(context));
}

/** Explicit inspection admission; never changes execution-account readiness. */
export function checkInstitutionalEntry(entry: UsTicketEntry, rules: UsRules, context: UsAccountContext, nowMs: number): UsTicketCheck {
  const allowed = context.protocol === 'institutional'
    && (context.accountEnabled ? context.scopeUse === 'execution_bound' : context.scopeUse === 'read_only')
    && context.institutional?.connectionState === 'verified' && context.permissions.viewOwn;
  return checkEntryWithBlockers(entry, rules, context, nowMs, allowed ? [] : ['Draft check unavailable for this account.']);
}

function checkEntryWithBlockers(entry: UsTicketEntry, rules: UsRules, context: UsAccountContext, nowMs: number, blockers: string[]): UsTicketCheck {
  const fields: UsTicketCheck['fields'] = {};

  if (!entry.outcome) fields.outcome = 'Choose an outcome.';
  if (!entry.action) fields.action = 'Choose buy or sell.';
  else if (!(entry.action === 'buy' ? context.permissions.buy : context.permissions.sell)) {
    fields.action = `You are not permitted to ${entry.action} on this account.`;
  }

  const minimum = decimalUnits(rules.minimumQuantity);
  const step = decimalUnits(rules.quantityStep);
  const tick = decimalUnits(rules.priceTick);
  if (minimum === null || step === null || tick === null || minimum <= 0n || step <= 0n || tick <= 0n || tick >= SCALE) {
    blockers.push('This market’s order rules are unusable, so no order can be checked.');
  }

  const quantity = decimalUnits(entry.quantity.trim());
  if (!entry.quantity.trim()) fields.quantity = 'Enter a number of contracts.';
  else if (quantity === null) fields.quantity = 'Enter contracts as a plain number, like 10 or 1.5.';
  else if (minimum !== null && quantity < minimum) fields.quantity = `Minimum is ${rules.minimumQuantity} contracts.`;
  else if (step !== null && step > 0n && quantity % step !== 0n) {
    fields.quantity = `Contracts must be a multiple of ${rules.quantityStep}.`;
  }

  const price = decimalUnits(entry.outcomePrice.trim());
  if (!entry.outcomePrice.trim()) fields.price = 'Enter a price.';
  else if (price === null) fields.price = 'Enter a price in dollars, like 0.40.';
  else if (price < PRICE_MIN || price > PRICE_MAX) fields.price = 'Price must be between $0.01 and $0.99.';
  else if (tick !== null && tick > 0n && entry.outcome) {
    // The venue's tick grid is defined on the YES price. The ticket still
    // sends this outcome's own price; the relay alone converts it.
    const yesEquivalent = entry.outcome === 'yes' ? price : SCALE - price;
    if (yesEquivalent % tick !== 0n) fields.price = `Price must be on the ${rules.priceTick} tick.`;
  }

  const supported = durationOptions(rules);
  if (supported.length === 0) {
    blockers.push('This market offers no duration this ticket supports.');
  } else if (!entry.timeInForce) {
    fields.duration = 'Choose a duration.';
  } else if (!supported.some((d) => d.value === entry.timeInForce)) {
    fields.duration = 'This market does not offer that duration.';
  }

  if (entry.timeInForce === GTD) {
    const iso = utcExpiry(entry.expiry);
    if (!entry.expiry) fields.expiry = 'Good till date needs an expiry (UTC).';
    else if (!iso) fields.expiry = 'Enter a complete UTC date and time.';
    else if (Date.parse(iso) <= nowMs) fields.expiry = 'Expiry must be in the future.';
  }

  if (entry.postOnly) {
    if (!rules.supportsPostOnly) fields.postOnly = 'This market does not support post-only.';
    else if (entry.timeInForce && IMMEDIATE.includes(entry.timeInForce)) {
      fields.postOnly = 'Post-only cannot be combined with an immediate duration.';
    }
  }

  return { fields, blockers };
}

export function isClean(check: UsTicketCheck): boolean {
  return check.blockers.length === 0 && Object.keys(check.fields).length === 0;
}

/**
 * The exact draft a clean entry describes. Text is sent as typed (trimmed),
 * never normalised, and `goodTillTime` is present only for GTD.
 */
export function buildDraft(
  entry: UsTicketEntry,
  scope: UsScope,
  target: UsTarget,
  draftRevision: string,
): UsDraft {
  const draft: UsDraft = {
    scope: { venue: scope.venue, scopeKey: scope.scopeKey, revision: scope.revision },
    target: { ...target },
    action: entry.action!,
    quantity: entry.quantity.trim(),
    outcomePrice: entry.outcomePrice.trim(),
    timeInForce: entry.timeInForce!,
    postOnly: entry.postOnly,
    draftRevision,
  };
  if (entry.timeInForce === GTD) draft.goodTillTime = utcExpiry(entry.expiry)!;
  return draft;
}

// ── Order economics (estimate) ─────────────────────────────────────────────

/** The Polymarket US standard taker rate, 6.95%, in basis points of p × (1 − p). */
const TAKER_RATE_BPS = 695n;
const BPS = 10_000n;

/** n / d rounded half-even (n ≥ 0, d > 0). */
function roundHalfEven(n: bigint, d: bigint): bigint {
  const q = n / d;
  const twiceRemainder = (n % d) * 2n;
  return q + (twiceRemainder > d || (twiceRemainder === d && q % 2n === 1n) ? 1n : 0n);
}

/**
 * What the K/P ticket shows before Place Order, computed locally, in whole
 * cents. `feeCents` is the standard taker fee at the limit price, the same
 * figure as the relay's `limitPriceTakerEstimateUsd`: 0.0695 × quantity ×
 * p × (1 − p), half-even to the cent. It is an estimate: a maker may pay less,
 * price improvement changes it, and the venue charges the actual fee.
 */
export type UsOrderEconomics =
  | { action: 'buy'; feeCents: bigint; totalCostCents: bigint; maxPayoutCents: bigint; maxProfitCents: bigint }
  | { action: 'sell'; feeCents: bigint; proceedsCents: bigint };

/**
 * Economics for an entry whose side, contracts and price are usable, else null.
 * `outcomePrice` is the chosen outcome's own price; p × (1 − p) is symmetric,
 * so a NO at 0.60 carries the same fee as a YES at 0.40.
 */
export function estimateUsOrderEconomics(
  entry: Pick<UsTicketEntry, 'action' | 'quantity' | 'outcomePrice'>,
): UsOrderEconomics | null {
  const quantity = decimalUnits(entry.quantity.trim());
  const price = decimalUnits(entry.outcomePrice.trim());
  if (!entry.action || quantity === null || price === null || quantity <= 0n || price < PRICE_MIN || price > PRICE_MAX) {
    return null;
  }
  const feeCents = roundHalfEven(TAKER_RATE_BPS * quantity * price * (SCALE - price) * 100n, BPS * SCALE ** 3n);
  // quantity × price is in units of 1e-24 dollars.
  const notionalCents = roundHalfEven(quantity * price * 100n, SCALE * SCALE);
  if (entry.action === 'sell') return { action: 'sell', feeCents, proceedsCents: notionalCents - feeCents };
  const totalCostCents = notionalCents + feeCents;
  const maxPayoutCents = roundHalfEven(quantity * 100n, SCALE);
  return { action: 'buy', feeCents, totalCostCents, maxPayoutCents, maxProfitCents: maxPayoutCents - totalCostCents };
}

/** Whole cents → `$1,234.56`; `signed` puts `+` on a positive amount, as K/P's Max Profit does. */
export function formatUsdCents(cents: bigint, signed = false): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const dollars = (abs / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const text = `$${dollars}.${(abs % 100n).toString().padStart(2, '0')}`;
  if (negative) return `-${text}`;
  return signed && cents > 0n ? `+${text}` : text;
}
